import { IonModal, IonPage, useIonRouter } from '@ionic/react';
import { slugifyModalTitle } from 'components/modal/HkModalHeader';
import {
  HKPlatform,
  INVENTORY_COLORS_PRODUCT_GROUP_ID,
  INVENTORY_HOME_SELECTIONS_PATH,
  INVENTORY_OPTIONS_PRODUCT_GROUP_ID
} from 'core/constants';
import { RootState } from 'core/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  hideAddItemModal,
  hideItemDetailsModal,
  hideSelectionDetailsModal,
  resetInventoryDetails,
  resetProductGroup,
  resetSearchedProducts,
  showAddItemModal,
  showDesktopItemDetails,
  showItemDetailsModal,
  showSelectionDetailsModal
} from 'features/shared/inventory/store/InventorySlice';
import {
  fetchSearchedProducts,
  requestProductGroupCount
} from 'features/shared/inventory/store/InventoryActions';
import InventoryAddProduct from 'features/shared/inventory/components/InventoryAddProduct';
import InventoryDetailsMobile from 'features/shared/inventory/components/InventoryDetailsMobile';
import ColorSelectionDetailsMobile from 'features/shared/inventory/selections/color-selections/components/ColorSelectionDetailsMobile';
import HomeSelectionDetailsMobile from 'features/shared/inventory/selections/home-selections/components/HomeSelectionDetailsMobile';
import { gaBtnClickEvent } from 'core/util';
import SearchedProductsGroupListView from 'features/shared/inventory/components/SearchedProductsGroupListView';
import InventoryGroupListViewDesktop from 'features/shared/inventory/components/InventoryGroupListViewDesktop';
import InventoryGroupListViewMobile from 'features/shared/inventory/components/InventoryGroupListViewMobile';

export { ProductGroups } from 'features/shared/inventory/utils/inventoryGroupIcons';

const InventoryGroups: React.FC = () => {
  const {
    loading,
    productGroups,
    searchedProducts,
    addItemModalVisible,
    addItemShowLanding,
    itemDetailsModalVisible,
    selectionDetailsModalVisible,
    selectionDetailsType
  } = useSelector((state: RootState) => state.inventory);
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const [search, setSearch] = useState('');
  const router = useIonRouter();
  const dispatch = useDispatch();
  const isDesktop = platformType === HKPlatform.DESKTOP && !!isDesktopWidth;

  const optionsGroupMeta = productGroups?.find(
    (g) => g.id === 67 // INVENTORY_OPTIONS_PRODUCT_GROUP_ID
  );
  const colorsGroupMeta = productGroups?.find(
    (g) => g.id === 68 // INVENTORY_COLORS_PRODUCT_GROUP_ID
  );
  // Home Option Selections (67) and Home Color Selections (68) are two
  // separate top-level cards (RHC10-15411) — each with its own count, not a
  // combined "Home Selections" card that nests Colors as a click-through.
  const optionsCount = optionsGroupMeta?.count ?? 0;
  const colorsCount = colorsGroupMeta?.count ?? 0;
  const standardProductGroups =
    productGroups?.filter((g) => g.id !== 67 && g.id !== 68) ?? [];
  // Total item count shown in the redesigned Inventory Landing header
  // (Figma "Inventory Landing" — trailing "N Items" text).
  const totalItemCount =
    optionsCount +
    colorsCount +
    standardProductGroups.reduce((sum, g) => sum + (g.count || 0), 0);

  const filteredInventory = (() => {
    const seen = new Set<number>();
    const groups: { id: number; name: string }[] = [];
    searchedProducts.forEach((p) => {
      if (p.group_id !== undefined && !seen.has(p.group_id)) {
        seen.add(p.group_id);
        groups.push({ id: p.group_id, name: p.group_name });
      }
    });
    return groups;
  })();

  const showGroup = (id: number) => {
    dispatch(resetProductGroup());
    router.push(`/inventory/group/${id}`, 'forward', 'push');
  };

  const goToInventoryDetailsPage = (groupId: number, productId: number) => {
    if (groupId !== undefined) {
      dispatch(resetInventoryDetails());
      dispatch(showDesktopItemDetails(productId));
      router.push(
        `/inventory/group/${groupId}/product/${productId}`,
        'forward',
        'push'
      );
    }
  };

  const openAddItemModal = () => {
    gaBtnClickEvent('add_item');
    dispatch(showAddItemModal());
  };

  const closeTask = () => {
    if (homeId) dispatch(requestProductGroupCount(homeId));
    dispatch(hideAddItemModal());
  };

  const showDetailsModal = (id: number, groupId: number) => {
    dispatch(resetInventoryDetails());
    if (groupId === INVENTORY_OPTIONS_PRODUCT_GROUP_ID) {
      dispatch(showSelectionDetailsModal({ productId: id, type: 'option' }));
    } else if (groupId === INVENTORY_COLORS_PRODUCT_GROUP_ID) {
      dispatch(showSelectionDetailsModal({ productId: id, type: 'color' }));
    } else {
      dispatch(showItemDetailsModal(id));
    }
  };

  const closeSelectionDetailsModal = () => {
    dispatch(hideSelectionDetailsModal());
  };

  const closeDetailsModal = () => {
    if (homeId) dispatch(requestProductGroupCount(homeId));
    dispatch(hideItemDetailsModal());
  };

  useEffect(() => {
    if (homeId) dispatch(requestProductGroupCount(homeId));
  }, [dispatch, homeId]);

  useEffect(() => {
    if (homeId && search) {
      dispatch(fetchSearchedProducts(homeId, search));
    } else {
      dispatch(resetSearchedProducts());
    }
  }, [search]);

  const searchResultsView = (
    <SearchedProductsGroupListView
      searchedProducts={searchedProducts}
      filteredInventory={filteredInventory}
      isDesktop={isDesktop}
      onNavigateToDetail={goToInventoryDetailsPage}
      onShowModal={showDetailsModal}
    />
  );

  return (
    <IonPage
      className={isDesktop ? 'hk-desktop-digital-inventory-group-page' : ''}
    >
      {isDesktop ? (
        <InventoryGroupListViewDesktop
          loading={loading}
          productGroups={productGroups}
          search={search}
          onSearchChange={setSearch}
          onAddItem={openAddItemModal}
          optionsCount={optionsCount}
          colorsCount={colorsCount}
          standardProductGroups={standardProductGroups}
          totalItemCount={totalItemCount}
          onShowGroup={showGroup}
          onNavigateToHomeSelections={() =>
            router.push(INVENTORY_HOME_SELECTIONS_PATH, 'forward', 'push')
          }
          onNavigateToColorSelections={() =>
            router.push(
              `/inventory/group/${INVENTORY_COLORS_PRODUCT_GROUP_ID}`,
              'forward',
              'push'
            )
          }
          searchResultsView={searchResultsView}
        />
      ) : (
        <InventoryGroupListViewMobile
          loading={loading}
          productGroups={productGroups}
          search={search}
          onSearchChange={setSearch}
          onAddItem={openAddItemModal}
          optionsCount={optionsCount}
          colorsCount={colorsCount}
          standardProductGroups={standardProductGroups}
          onShowGroup={showGroup}
          onNavigateToHomeSelections={() =>
            router.push(INVENTORY_HOME_SELECTIONS_PATH, 'forward', 'push')
          }
          onNavigateToColorSelections={() =>
            router.push(
              `/inventory/group/${INVENTORY_COLORS_PRODUCT_GROUP_ID}`,
              'forward',
              'push'
            )
          }
          searchResultsView={searchResultsView}
        />
      )}
      <IonModal
        className={`hk-digital-inventory-add-product-modal${
          addItemShowLanding ? ' hk-inventory-add-item-landing-active' : ''
        }`}
        isOpen={addItemModalVisible}
        backdropDismiss={false}
        onDidDismiss={closeTask}
        aria-labelledby={slugifyModalTitle('Add an Item')}
      >
        <InventoryAddProduct />
      </IonModal>
      <IonModal
        className="hk-digital-inventory-details-modal"
        isOpen={itemDetailsModalVisible}
        onDidDismiss={closeDetailsModal}
      >
        <InventoryDetailsMobile />
      </IonModal>
      <IonModal
        className="hk-digital-inventory-details-modal"
        isOpen={selectionDetailsModalVisible}
        onDidDismiss={closeSelectionDetailsModal}
      >
        {selectionDetailsType === 'color' ? (
          <ColorSelectionDetailsMobile />
        ) : (
          <HomeSelectionDetailsMobile />
        )}
      </IonModal>
    </IonPage>
  );
};

export default InventoryGroups;
